// ── PLANNING A RENEWAL SEASON'S FARM VISITS ──────────────────────────────────
//
// Kevin, 2026-10-03: *"build a farm visit route optimizer. During renewal season we may go
// to 2-3 farms per day. Plan optimal customer groups so I can plan out a schedule, and
// make the trips as efficient as possible. You should have all of their addresses. Make it
// flexible so I can say how many farms I want to visit per day and then you select the
// farms by proximity and visit efficiency."*
//
// ⚠⚠ THE DISTANCES HERE ARE STRAIGHT-LINE, AND THE PAGE SAYS SO. There is no routing
// service in this app and no key for one, so every figure below is great-circle distance
// between two farm coordinates. That is a good basis for deciding WHICH farms belong in a
// day together — proximity is proximity — and it is NOT a drive time: it ignores roads,
// the Finger Lakes, and the fact that two farms 8 miles apart across Cayuga Lake are a
// 40-minute drive. Printing a minutes figure off it would be inventing a number. The
// result carries miles only, labelled as the crow flies.
//
// ⚠ AND 48 CUSTOMERS CANNOT BE ROUTED AT ALL. Measured 2026-10-03 over the live book: 282
// farmers, 207 with a lat and lon, 27 with an address and no coordinates (geocodable), and
// 48 with NEITHER. A planner that silently dropped those would hand Kevin a season plan
// that misses 48 customers and looks complete, which is the costlier of the two errors by
// a long way. They come back in `unplaceable`, named, with which half they are missing.

"use strict";

// ⚠ A STATED DEFAULT, NOT A MEASUREMENT. Nothing in this app records how far Kevin is
// willing to drive between two farms in a day. 60 straight-line miles is roughly an hour of
// rural driving in this book's territory and it is EDITABLE on screen, which is the honest
// shape for a number nobody has sourced: visible, adjustable, and named as a setting rather
// than buried as a constant. What it must not be is absent — see the note in planVisits.
const DEFAULT_MAX_LINK_MILES = 60;

// ⚠ WHERE THE DAY STARTS AND ENDS. Kevin, 2026-10-04, asked for days that work "from
// furthest away and then back towards office" and gave the address as 1112 East River Road,
// Avon. Geocoded through the same OSM path the farm addresses use, matched to the house
// number: 42.93470, -77.73667.
//
// ⚠ IT IS THE SAME ADDRESS AS HOWLETT FARMS INC IN THE CUSTOMER BOOK, AND THAT IS CORRECT —
// CONFIRMED BY KEVIN, 2026-10-04 ("Yes it is the office"). It is written down because it
// looks exactly like a copy error: the identical address had been printed in the session
// minutes before he gave it, so the next reader has every reason to "fix" it. The agency
// and the farm share a location; do not reconcile this by changing the coordinates.
//
// Correct the two numbers here only if the office itself moves.
const AGENCY_ORIGIN = Object.freeze({ lat: 42.93470, lon: -77.73667, label: "1112 East River Road, Avon" });

// ⚠ TWO FARMS THIS CLOSE ARE ONE STOP, NOT TWO. Measured on the live CY2027 wheat roster
// 2026-10-04: at 1 mile there are 8 such pairs and every cluster is exactly TWO farms, so the
// rule can never ask for more seats than a day has. At 2 miles it is 24 pairs with clusters
// of 3; at 3 miles a cluster of NINE, which would dictate the plan rather than tidy it.
const TOGETHER_MILES = 1.0;
const R_MILES = 3958.7613;
const rad = (d) => (d * Math.PI) / 180;

/** Great-circle miles between two {lat, lon}. */
function milesBetween(a, b) {
  if (!a || !b) return null;
  const la = Number(a.lat), lo = Number(a.lon), lb = Number(b.lat), ob = Number(b.lon);
  if (![la, lo, lb, ob].every(Number.isFinite)) return null;
  const dLat = rad(lb - la), dLon = rad(ob - lo);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(rad(la)) * Math.cos(rad(lb)) * Math.sin(dLon / 2) ** 2;
  return 2 * R_MILES * Math.asin(Math.min(1, Math.sqrt(s)));
}

// ⚠ `Number(null)` IS 0 AND 0 IS FINITE. This read `Number.isFinite(Number(f.lat))`, so a
// farm with no coordinates at all passed and was planted at (0, 0) — the Gulf of Guinea.
// Measured on the live wheat CY2027 roster 2026-10-04, before the fix:
//
//   · NINE farms with no location were put on the schedule as Days 66-70, paired with each
//     other because they were all at the same fake point and so looked 0 miles apart;
//   · the "could not be placed" list read EMPTY while naming none of them — the one list
//     whose whole job is to say who was left out;
//   · and the book centre, which seeds every day as "furthest from centre", was dragged
//     364 MILES into the Atlantic (42.34,-77.94 -> 39.27,-72.28). That reshuffles the whole
//     construction, which is how ANDERSON FARMS DAIRY ended up paired across the county
//     instead of with LEFEBER 0.48 mi away. Kevin spotted it from the territory.
//
// (0, 0) is refused outright for the same reason: it is what an unpopulated column looks
// like once something has coerced it, and no farm in this book is off West Africa. The same
// rule is stated in src/renewals/farmLocation.js `point()`; this is the second caller of it.
const hasCoords = (f) => {
  if (!f || f.lat == null || f.lon == null || f.lat === "" || f.lon === "") return false;
  const la = Number(f.lat), lo = Number(f.lon);
  if (!Number.isFinite(la) || !Number.isFinite(lo)) return false;
  return !(la === 0 && lo === 0);
};

/**
 * Order a day's stops to minimise travel.
 *
 * Exact for the sizes this is actually used at. Kevin said 2-3 farms a day; at n <= 7 every
 * permutation is cheap (5,040 worst case) and gives the true optimum, so there is no reason
 * to approximate. Above that it falls back to nearest-neighbour plus 2-opt, and SAYS which
 * it used — an "optimal" label on a heuristic is a claim the code cannot support.
 */
function orderStops(stops, origin) {
  const n = stops.length;
  const leg0 = (a, b) => milesBetween(a, b) || 0;
  // ⚠ A ONE-FARM DAY IS NOT A ZERO-MILE DAY ONCE THERE IS AN OFFICE. BRIDGEWATER FARMING is
  // 365 miles from Avon; reporting that day as "0 mi" because it has no farm-to-farm leg
  // hides the only thing worth knowing about it — it is a 730-mile round trip and probably
  // an overnight, not a day.
  if (n <= 1) {
    return { order: stops.slice(),
             miles: origin && n === 1 ? +(2 * leg0(origin, stops[0])).toFixed(1) : 0,
             method: "trivial" };
  }

  const leg = (a, b) => milesBetween(a, b) || 0;
  // ⚠ WITH AN OFFICE, A DAY IS A LOOP, NOT A ONE-WAY TRIP. It used to score
  // office -> s1 -> ... -> sn and stop, which leaves the drive home out of the day's miles
  // and lets the route end anywhere. Scoring the return leg is both more honest about the
  // day and what makes the orientation below free.
  const total = (seq) => {
    let m = 0, prev = origin || seq[0];
    let first = true;
    for (const s of seq) {
      if (origin || !first) m += leg(prev, s);
      prev = s; first = false;
    }
    if (origin && seq.length) m += leg(seq[seq.length - 1], origin);
    return m;
  };
  // ── WORK OUTWARD, THEN HOME ────────────────────────────────────────────────
  // Kevin, 2026-10-04: *"Within a day, I do like to work from furthest away and then back
  // towards office."* Taken literally: the stops run in DESCENDING distance from the office,
  // so the long drive is done first thing and each call brings him nearer home.
  //
  // ⚠ THAT IS NOT THE CHEAPEST ROUTE, AND THE COST WAS MEASURED BEFORE CHOOSING IT. The
  // minimal loop puts the furthest farm in the MIDDLE — out, round, back — so orienting the
  // cheapest loop is not the same thing: measured on the live CY2027 wheat roster, 3/day,
  // the cheapest loop runs Hansen 32.5 mi -> Austic 57.9 -> Bay 28.3, which never "starts
  // furthest". Across all 38 multi-stop days strict ordering costs 56 miles, 1.0%, and
  // reorders 20 of them — while actually LOWERING farm-to-farm miles (660 vs 721), since the
  // extra falls on the office legs. A 1% premium for the pattern he actually drives is a
  // trade worth making, and the alternative is a route he would re-order by hand anyway.
  if (origin) {
    const order = stops.slice().sort((a, b) => leg(origin, b) - leg(origin, a));
    return { order, miles: +total(order).toFixed(1), method: "furthest first, back toward the office" };
  }

  if (n <= 7) {
    let best = null, bestM = Infinity;
    const perm = (arr, cur) => {
      if (!arr.length) {
        const m = total(cur);
        if (m < bestM) { bestM = m; best = cur.slice(); }
        return;
      }
      for (let i = 0; i < arr.length; i++) {
        const rest = arr.slice(0, i).concat(arr.slice(i + 1));
        cur.push(arr[i]); perm(rest, cur); cur.pop();
      }
    };
    perm(stops, []);
    return { order: best, miles: +bestM.toFixed(1), method: "exact" };
  }

  // Nearest neighbour, then 2-opt until it stops improving.
  const left = stops.slice();
  const seq = [];
  let cur = origin || left.shift();
  if (!origin) seq.push(cur);
  while (left.length) {
    let bi = 0, bd = Infinity;
    for (let i = 0; i < left.length; i++) { const d = leg(cur, left[i]); if (d < bd) { bd = d; bi = i; } }
    cur = left.splice(bi, 1)[0];
    seq.push(cur);
  }
  let improved = true;
  while (improved) {
    improved = false;
    for (let i = 0; i < seq.length - 1; i++) {
      for (let k = i + 1; k < seq.length; k++) {
        const cand = seq.slice(0, i).concat(seq.slice(i, k + 1).reverse(), seq.slice(k + 1));
        if (total(cand) + 1e-9 < total(seq)) { seq.splice(0, seq.length, ...cand); improved = true; }
      }
    }
  }
  return { order: seq, miles: +total(seq).toFixed(1), method: "nearest-neighbour + 2-opt" };
}

/**
 * Group farms into days of `perDay` and order each day.
 *
 * The grouping is seeded from the farm FURTHEST from the centre of the book and takes its
 * nearest neighbours — the standard sweep for this shape, and the reason is worth stating:
 * seeding from the middle leaves the outliers to be swept up last, in a day that criss-
 * crosses the whole territory. Starting at the edge keeps the long drives inside one day
 * rather than spreading them across several.
 */
function planVisits({ farms, perDay = 3, origin = null, maxDays = null, maxLinkMiles = DEFAULT_MAX_LINK_MILES }) {
  const want = Math.max(1, Math.min(12, Math.round(Number(perDay) || 3)));
  // ⚠ A DAY HAS A REACH, AND WITHOUT ONE THE PLANNER INVENTS DAYS NOBODY CAN DRIVE.
  // Measured on the live book 2026-10-03, planning 124 outstanding wheat customers at 3 a
  // day with no limit: Day 1 was BRIDGEWATER FARMING (Steuben NY) + FAHNCKE (Auglaize OH) +
  // LAPE (Somerset PA) — 345.5 miles, longest leg 269.5, three states. Nothing was wrong
  // with the grouping; it took the most remote farm and its two nearest neighbours, and in a
  // book spanning NY/OH/PA/MD the nearest neighbour of a remote farm is still hours away.
  // So a day stops growing when the next farm is further than this, and the day is SHORT and
  // says why rather than being padded with a farm in another state.
  const link = Number(maxLinkMiles);
  const reach = Number.isFinite(link) && link > 0 ? link : Infinity;
  const all = Array.isArray(farms) ? farms : [];

  // ⚠ NAMED, NEVER DROPPED. A plan that quietly omits the farms it cannot place reads as a
  // complete season.
  const placeable = [], unplaceable = [];
  for (const f of all) {
    if (hasCoords(f)) placeable.push(f);
    else unplaceable.push({
      ...f,
      why: f && f.address ? "has an address but no coordinates — geocode it to include it"
                          : "no address and no coordinates on file",
    });
  }

  if (!placeable.length) {
    return { days: [], perDay: want, placed: 0, unplaceable, totalMiles: 0,
             why: unplaceable.length ? "no customer in this set has coordinates" : "no customers in this set" };
  }

  const cx = placeable.reduce((s, f) => s + Number(f.lat), 0) / placeable.length;
  const cy = placeable.reduce((s, f) => s + Number(f.lon), 0) / placeable.length;
  const centre = { lat: cx, lon: cy };

  const pool = placeable.slice();
  const days = [];
  // The raw membership, before stops are ordered — the improvement pass below works on this,
  // because swapping a farm out of an already-ordered day leaves a route through a farm that
  // is no longer in it.
  const groups = [];
  while (pool.length) {
    // Seed: whichever remaining farm is furthest from the book's centre.
    let si = 0, sd = -1;
    for (let i = 0; i < pool.length; i++) {
      const d = milesBetween(centre, pool[i]) || 0;
      if (d > sd) { sd = d; si = i; }
    }
    const seed = pool.splice(si, 1)[0];
    const group = [seed];
    while (group.length < want && pool.length) {
      let bi = 0, bd = Infinity;
      for (let i = 0; i < pool.length; i++) {
        // Distance to the NEAREST farm already in the day, not to the seed — a day is a
        // chain of stops, so the right question is how far this farm is from the group.
        let near = Infinity;
        for (const g of group) { const d = milesBetween(g, pool[i]); if (d != null && d < near) near = d; }
        if (near < bd) { bd = near; bi = i; }
      }
      // The day stops here rather than reaching across the territory for a third farm.
      if (bd > reach) break;
      group.push(pool.splice(bi, 1)[0]);
    }
    groups.push(group);
    if (maxDays && groups.length >= maxDays) break;
  }

  // ── FINE TUNING: SWAP FARMS BETWEEN DAYS WHILE IT HELPS ────────────────────
  // ⚠ GREEDY CONSTRUCTION SEPARATES NEIGHBOURS, AND A HUMAN WHO KNOWS THE TERRITORY SEES IT
  // IMMEDIATELY. Each day is grown from a seed and its members are removed from the pool, so
  // two farms half a mile apart can land in different days purely because an earlier day got
  // to one of them first. Kevin, 2026-10-04, reading a plan: *"LeFeber and Anderson should
  // never be different days because they are so close"* — 0.5 miles apart.
  //
  // This pass takes the finished days and swaps any two farms between them whenever that
  // lowers the pair's combined driving, repeating until nothing improves. It is a strict
  // improvement by construction — a swap is only kept if the total falls — and it changes
  // neither the number of days nor which farms are planned, so it cannot cost a day or
  // strand anybody. Measured on the live CY2027 wheat roster:
  //
  //        2/day reach 15 mi   255 -> 212 mi  (-17%)   66 days, 17 singles, unchanged
  //        3/day reach 60 mi   606 -> 545 mi  (-10%)   41 days,  3 singles, unchanged
  //
  // 31 ms for 66 days; 345 ms for the whole 217-farm book. It runs on COMMIT, not per
  // keystroke, so that cost is paid once when a control is changed.
  //
  // ⚠ IT RUNS BEFORE THE STOPS ARE ORDERED AND BEFORE THE DAYS ARE SORTED, because both of
  // those read the final membership: ordering a day and then moving a farm out of it leaves
  // a route through a farm that is no longer there, and sorting by the biggest customer
  // before a swap can move that customer is a sort of the wrong thing.
  // ── NEIGHBOURS STAY TOGETHER, EVEN WHERE SPLITTING THEM WOULD DRIVE LESS ───
  // Kevin, 2026-10-04, reading a plan: *"LeFeber and Anderson should never be different
  // days because they are so close."* They are 0.48 miles apart. A pure mileage optimiser
  // does NOT guarantee that and was right not to on its own terms — measured over those six
  // farms at 3/day, pairing them forces the other four into worse company (17.6 mi against
  // the 15.0 it chose). But "two farms half a mile apart are effectively one stop" is a
  // judgement about how the day is actually worked, not about great-circle distance, and it
  // is the agent's to make.
  //
  // So it is a LEXICOGRAPHIC rule, not a weight: a swap is taken if it reunites a close pair
  // even at more miles, and only then on miles. A weight would need a number nobody sourced
  // and would trade neighbours away at some exchange rate.
  //
  // ⚠ IT IS SAFE BECAUSE IT IS TINY, and that was measured before it was written. On the
  // live CY2027 wheat roster: within 1 mile there are 8 pairs forming 8 clusters of exactly
  // TWO farms — no cluster is larger, so the rule can never demand more seats than a day
  // has. Exactly ONE of those 8 is split today, at 2/day and at 3/day alike. Widening to 2
  // miles would cover 24 pairs in clusters of up to 3, and to 3 miles a cluster of NINE,
  // which stops being "one stop" and starts dictating the plan.
  const closePairs = [];
  for (let i = 0; i < placeable.length; i++) {
    for (let j = i + 1; j < placeable.length; j++) {
      const dd = milesBetween(placeable[i], placeable[j]);
      if (dd != null && dd <= TOGETHER_MILES) closePairs.push([placeable[i], placeable[j]]);
    }
  }
  // Recomputed in full on every candidate swap, which is free: the list is 8 pairs on the
  // live book, not 115 farms squared.
  const splitCount = (gs) => {
    const where = new Map();
    gs.forEach((g, idx) => g.forEach((f) => where.set(f.id, idx)));
    let n = 0;
    for (const [a, b] of closePairs) if (where.get(a.id) !== where.get(b.id)) n++;
    return n;
  };

  const dayDrive = (g) => {
    if (g.length < 2) return origin && g.length === 1 ? 2 * (milesBetween(origin, g[0]) || 0) : 0;
    return orderStops(g, origin).miles;
  };
  // A swap must not build a day that breaks the reach the user set — the whole point of
  // that control is that no day reaches further than they will drive.
  const withinReach = (g) => {
    if (g.length < 2) return true;
    for (const a of g) {
      let near = Infinity;
      for (const b of g) if (a !== b) { const x = milesBetween(a, b); if (x != null && x < near) near = x; }
      if (near > reach) return false;
    }
    return true;
  };
  for (let round = 0; round < 6; round++) {
    let moved = false;
    for (let i = 0; i < groups.length; i++) {
      for (let j = i + 1; j < groups.length; j++) {
        for (let a = 0; a < groups[i].length; a++) {
          for (let b = 0; b < groups[j].length; b++) {
            const A = groups[i].slice(), Bb = groups[j].slice();
            const before = dayDrive(A) + dayDrive(Bb);
            const splitBefore = splitCount(groups);
            const t = A[a]; A[a] = Bb[b]; Bb[b] = t;
            if (!withinReach(A) || !withinReach(Bb)) continue;
            const trial = groups.slice(); trial[i] = A; trial[j] = Bb;
            const splitAfter = splitCount(trial);
            const after = dayDrive(A) + dayDrive(Bb);
            // Reuniting neighbours outranks miles; miles decide only when it is a wash.
            const better = splitAfter < splitBefore
              || (splitAfter === splitBefore && after < before - 1e-9);
            if (better) { groups[i] = A; groups[j] = Bb; moved = true; }
          }
        }
      }
    }
    if (!moved) break;
  }

  for (const group of groups) {
    const ord = orderStops(group, origin);
    days.push({
      stops: ord.order,
      miles: ord.miles,
      method: ord.method,
      // The longest single hop, because a day of three farms averaging 12 miles with one
      // 40-mile leg is a different day from three evenly spread.
      // A day with fewer stops than asked for is SHORT, and the reason is carried: the
      // next farm was further than the day's reach. Silently handing back two farms where
      // three were asked for reads as a book that ran out of customers.
      short: group.length < want,
      longestLegMiles: (() => {
        let m = 0;
        for (let i = 1; i < ord.order.length; i++) {
          const d = milesBetween(ord.order[i - 1], ord.order[i]) || 0;
          if (d > m) m = d;
        }
        return +m.toFixed(1);
      })(),
    });
  }

  // ── WHICH DAY COMES FIRST ──────────────────────────────────────────────────
  // ⚠ ORDERING THE DAYS IS FREE; CHOOSING WHO SHARES ONE IS NOT. Kevin, 2026-10-04: *"I like
  // to get my biggest customers done first ... but to do those in an order that makes the
  // most sense for efficient visits."* Those pull against each other only if size is allowed
  // to decide the GROUPING. It is not: the days are built by proximity exactly as before and
  // size only sorts the finished days, so the geography is untouched.
  //
  // Measured on the live CY2027 wheat roster, 115 farms, 2/day reach 15 mi:
  //
  //                                    days  miles  singles   top-20 seen by day
  //   build by proximity, drive W->E     66    255       17   median 31, 2 in the first 10
  //   build by proximity, SORT BY SIZE   66    255       17   median  8, 13 in the first 10
  //   SEED on the biggest customer       67    267       19   median  9, 12 in the first 10
  //
  // Sorting is strictly better than seeding: the same answer on the thing Kevin asked for,
  // at no cost in days, miles or lonely days. Seeding on size costs a day and 12 miles and
  // buys nothing — so the grouping rule is left alone.
  days.sort((a, b) => {
    // The day's LARGEST customer, not its total: a day holding one big farm and one small is
    // a big-customer day, and summing would let two middling farms outrank it.
    //
    // ⚠ NO "are there sizes?" FLAG. The first cut guarded this with
    // `days.some(d => d.stops.some(f => Number(f.size) > 0))`, and mutation testing showed the
    // guard CANNOT change the answer: with no sizes every day scores 0, `bm !== am` is false,
    // and the geographic order below decides anyway. A flag that cannot alter behaviour is a
    // guard nobody can test and a reader has to reason about — so it is gone, and the
    // unpriced-renewal-year case is covered by the arithmetic rather than by a second rule.
    const am = Math.max(...a.stops.map((f) => Number(f.size) || 0));
    const bm = Math.max(...b.stops.map((f) => Number(f.size) || 0));
    if (bm !== am) return bm - am;
    if (origin) return (milesBetween(origin, a.stops[0]) || 0) - (milesBetween(origin, b.stops[0]) || 0);
    const al = a.stops.reduce((s, f) => s + Number(f.lon), 0) / a.stops.length;
    const bl = b.stops.reduce((s, f) => s + Number(f.lon), 0) / b.stops.length;
    return al - bl;
  });
  days.forEach((d, i) => { d.day = i + 1; });

  const leftOver = maxDays ? pool.length : 0;
  return {
    days,
    perDay: want,
    placed: days.reduce((s, d) => s + d.stops.length, 0),
    unplaceable,
    // ⚠ CAPPED BY maxDays MEANS FARMS WERE NOT PLANNED. Reported, not silently truncated.
    notPlanned: leftOver,
    totalMiles: +days.reduce((s, d) => s + d.miles, 0).toFixed(1),
    maxLinkMiles: reach === Infinity ? null : reach,
    shortDays: days.filter((d) => d.short).length,
    basis: "straight-line distance between farm coordinates — not drive time, and not road miles",
    why: null,
  };
}

module.exports = { milesBetween, orderStops, planVisits, hasCoords, DEFAULT_MAX_LINK_MILES, AGENCY_ORIGIN };
